import {
  Button,
  LoadingIndicator,
  Modal,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ReworkTaskRequest } from "../../../../amplify/backend/function/Cap/ts/public/types";
import { SplatProposal } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import {
  getProposals,
  getReworkTaskByName,
  getReworkTaskDetails,
  postReworkTask,
  postUpdateActiveProposal,
} from "cap/api/capApi";
import { useAppSelector } from "store";
import { reloadMFE, useNavigateHelper } from "utils/useNavigateHelper";
import { setCapRepRedesignReadyToPoll } from "cap/utils/capRepRedesignDocRefresh";

type RepricingLoadingWizardType = {
  refetchCapDocuments?: () => void;
  submitOfferSuccess: boolean;
  submitOfferFailure: Error | null;
  submittingOffer: boolean;
  initialTaskId: string;
  userId: string;
  determinantTaskId?: string;
  initialTaskName: string;
  taskEligible: boolean;
  isFromRedesignFlow?: boolean;
};

const PREPARE_CAP_TASK_NAME = "Prepare CAP Package";

const REPRICING_REWORK_DESCRIPTION =
  "Automatically entered, rep has repriced this CAP using the Rep Repricing @ CAP feature in the Sales App.";
const REDESIGN_REWORK_DESCRIPTION =
  "Automatically entered, rep has redesigned & repriced this CAP using the Rep Self-Serve (Redesign & Reprice) @ CAP feature in the Sales App.";

const RepricingLoadingWizard = ({
  refetchCapDocuments,
  submitOfferSuccess,
  submitOfferFailure = null,
  submittingOffer,
  initialTaskId,
  userId,
  determinantTaskId,
  initialTaskName,
  taskEligible,
  isFromRedesignFlow = false,
}: RepricingLoadingWizardType) => {
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const [reworkingTask, setReworkingTask] = useState(false);
  const { prospectId } = useParams();
  const [isLoan, setIsLoan] = useState(false);
  const [reworkTaskSuccess, setReworkTaskSuccess] = useState(false);
  const [reworkTaskError, setReworkTaskError] = useState<Error | null>(null);
  const [updatingActiveProposal, setUpdatingActiveProposal] = useState(false);
  const [otherError, setOtherError] = useState<Error | null>(null);
  const [updateActiveProposalSuccess, setUpdateActiveProposalSuccess] =
    useState(false);
  const [updateActiveProposalError, setUpdateActiveProposalError] =
    useState<Error | null>(null);
  const [newDeterminantTaskId, setNewDeterminantTaskId] = useState<
    string | null
  >(null);
  const [newTaskId, setNewTaskId] = useState<string | null>(null);
  const navigate = useNavigateHelper({ replace: true });
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();

  const findLatestProposal = () => {
    let selected: any;
    if (!prospectId) {
      return;
    }

    return getProposals(authKey, prospectId)
      .then((proposals) => {
        proposals?.forEach((p: SplatProposal) => {
          if (!selected) {
            selected = p;
            return;
          }
          const maxCreated = new Date(selected.createdDate).getTime();
          const currentCreated = new Date(p.createdDate).getTime();
          selected = currentCreated > maxCreated ? p : selected;
        });

        return selected;
      })
      .catch((err) => {
        setUpdateActiveProposalError(err);
      });
  };

  const updateAPHasntStarted =
    submittingOffer === false &&
    submitOfferSuccess === true &&
    reworkingTask === false &&
    updatingActiveProposal === false;

  const updateAPHandler = async () => {
    setUpdateActiveProposalError(null);
    setUpdateActiveProposalSuccess(false);
    setUpdatingActiveProposal(true);
    const latestProposal = await findLatestProposal();
    const { id, agreementType } = latestProposal;
    setIsLoan(agreementType === "Customer Owned - Bank Financed");
    if (!id) {
      return;
    }
    postUpdateActiveProposal(authKey, id)
      .then(() => {
        setUpdateActiveProposalSuccess(true);
        setUpdatingActiveProposal(false);
        if (!taskEligible) {
          recheckTaskDetails(initialTaskId, initialTaskName);
          return;
        }
        reworkHandler(initialTaskId, determinantTaskId as string);
      })
      .catch((err) => {
        setUpdatingActiveProposal(false);
        setUpdateActiveProposalError(err);
      });
  };

  const navigateToCapAndRefresh = () => {
    navigate(`/cap`);
    reloadMFE();
  };

  const reworkHandler = (taskId: string, detTaskId?: string) => {
    setReworkingTask(true);
    setReworkTaskError(null);
    setReworkTaskSuccess(false);

    const requestBody: ReworkTaskRequest = {
      allowProjectToBeTakenOffFastTrack: false,
      reworkDescription: isFromRedesignFlow
        ? REDESIGN_REWORK_DESCRIPTION
        : REPRICING_REWORK_DESCRIPTION,
      reworkReason: "Customer Agreement Change",
      reworkSubReason: "Agreement Type Change",
      userId,
      determinantTaskId: detTaskId,
    };

    postReworkTask(authKey, taskId, requestBody)
      .then(() => {
        setReworkingTask(false);
        setReworkTaskSuccess(true);
      })
      .catch((err) => {
        setReworkingTask(false);
        setReworkTaskError(err);
      });
  };

  const recheckTaskDetails = (taskId: string, taskName: string) => {
    getReworkTaskDetails(authKey, taskId)
      .then((taskDetails) => {
        const { ReworkTaskDetails } = taskDetails;
        const { reworkEligible, reworkIneligibleReason, determinantTaskList } =
          ReworkTaskDetails;
        let newDetTaskId;

        if (determinantTaskList) {
          newDetTaskId = Object.keys(determinantTaskList)[0];
        }
        setNewDeterminantTaskId(newDeterminantTaskId);
        if (reworkEligible) {
          reworkHandler(taskId, newDetTaskId);
          return;
        } else if (taskName === PREPARE_CAP_TASK_NAME) {
          setOtherError(new Error(reworkIneligibleReason));
        } else {
          getReworkTaskByName(
            authKey,
            prospectId as string,
            PREPARE_CAP_TASK_NAME
          )
            .then((data) => {
              const task = data[0];
              setNewTaskId(task.taskId);
              recheckTaskDetails(task.taskId, PREPARE_CAP_TASK_NAME);
            })
            .catch((err) => {
              setOtherError(err);
            });
        }
      })
      .catch((err) => {
        setOtherError(err);
      });
  };

  useEffect(() => {
    if (updateAPHasntStarted) {
      updateAPHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitOfferSuccess]);

  useEffect(() => {
    if (
      submitOfferSuccess &&
      reworkTaskSuccess &&
      updateActiveProposalSuccess
    ) {
      refetchCapDocuments?.();
      if (isFromRedesignFlow && prospectId) {
        setCapRepRedesignReadyToPoll(prospectId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitOfferSuccess, reworkTaskSuccess, updateActiveProposalSuccess]);

  return (
    <BigModal
      type="warning"
      title="Do not close this page - wait until all steps below are completed"
      onClose={() => {}}
      testId="repricing-loading-wizard-modal"
      hideClose
    >
      <GridContainer isMobile={isMobile} isTablet={isTablet}>
        <WorkflowPreviewSection
          title="Saving offer"
          stepNum={1}
          loading={submittingOffer}
          success={submitOfferSuccess}
          error={submitOfferFailure}
        />
        <WorkflowPreviewSection
          title="Updating Project"
          stepNum={2}
          loading={updatingActiveProposal}
          success={updateActiveProposalSuccess}
          error={updateActiveProposalError}
          resubmit={updateAPHandler}
        />
        <WorkflowPreviewSection
          title="Reworking CAP"
          stepNum={3}
          loading={reworkingTask}
          success={reworkTaskSuccess}
          error={reworkTaskError || otherError}
          resubmit={() => {
            if (otherError) {
              setOtherError(null);
              newTaskId
                ? recheckTaskDetails(newTaskId, PREPARE_CAP_TASK_NAME)
                : recheckTaskDetails(initialTaskId, initialTaskName);
            } else if (newDeterminantTaskId && newTaskId) {
              reworkHandler(newTaskId, newDeterminantTaskId);
            } else {
              reworkHandler(initialTaskId, determinantTaskId as string);
            }
          }}
        />
      </GridContainer>
      <GapContainer isMobile={isMobile} isTablet={isTablet}>
        {isLoan && (
          <Typography size={isMobile || isTablet ? 12 : 16}>
            If you have changed the loan amount and need to send new loan
            documents to the customer please reach out to the Bank Finance team.
            Text: <a href="sms:385-317-0250">385.317.0250</a> Email:{" "}
            <a href="mailto:postsra@sunrun.com">postsra@sunrun.com</a>
          </Typography>
        )}
        {submitOfferSuccess &&
          reworkTaskSuccess &&
          updateActiveProposalSuccess && (
            <>
              <Typography size={isMobile || isTablet ? 12 : 16}>
                Success, your new offer has been saved. It may take a minute or
                two for your new CAP to be ready to present after you refresh
                your browser.
              </Typography>
              <Button onClick={() => navigateToCapAndRefresh()}>
                {isFromRedesignFlow
                  ? "Return to CAP then REFRESH your browser to see updates"
                  : "Return to CAP"}
              </Button>
            </>
          )}
      </GapContainer>
    </BigModal>
  );
};

type WorkflowPreviewSectionProps = {
  title: string;
  stepNum: number;
  loading: boolean;
  success: boolean;
  error: Error | null;
  resubmit?: () => void;
};

const WorkflowPreviewSection = ({
  resubmit,
  title,
  stepNum,
  loading,
  success,
  error,
}: WorkflowPreviewSectionProps) => {
  const notActivated = !loading && !error && !success;
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();

  return (
    <section>
      <Typography
        align="center"
        variant={isMobile || isTablet ? "h5" : "h4"}
        color={notActivated ? tokens.TINTS_OFF_WHITE_30 : tokens.BRAND_HEROBLUE}
      >
        {title}
      </Typography>
      <Typography
        align="center"
        color={
          notActivated ? tokens.TINTS_OFF_WHITE_30 : tokens.TINTS_OFF_WHITE_20
        }
      >
        Step {stepNum} of 3
      </Typography>
      <MiddleContentSection isMobile={isMobile} isTablet={isTablet}>
        {loading && (
          <MiddleContentSection isMobile={isMobile} isTablet={isTablet}>
            <LoadingIndicator color="black" message="In Progress..." />
          </MiddleContentSection>
        )}
        {success && (
          <MiddleContentSection isMobile={isMobile} isTablet={isTablet}>
            <Typography
              color={tokens.TINTS_SUCCESS_50}
              variant={isMobile || isTablet ? "p" : "h4"}
              align="center"
            >
              Success
            </Typography>
            <SvgIcon
              name={SvgNames.CheckEnclosed}
              width={isMobile || isTablet ? 25 : 75}
              height={isMobile || isTablet ? 25 : 75}
              color={tokens.TINTS_SUCCESS_50}
            />
          </MiddleContentSection>
        )}
        {error && (
          <MiddleContentSection isMobile={isMobile} isTablet={isTablet}>
            <Typography
              color={tokens.TINTS_ERROR_40}
              variant={isMobile || isTablet ? "p" : "h4"}
              align="center"
            >
              Error
            </Typography>
            <SvgIcon
              name={SvgNames.ErrorFilled}
              width={isMobile || isTablet ? 25 : 75}
              height={isMobile || isTablet ? 25 : 75}
              color={tokens.TINTS_ERROR_40}
            />
            <Typography color={tokens.TINTS_ERROR_40} align="center">
              {error?.message}
            </Typography>
          </MiddleContentSection>
        )}
        {notActivated && (
          <Typography align="center" color={tokens.TINTS_OFF_WHITE_30}>
            Waiting for previous step to complete
          </Typography>
        )}
      </MiddleContentSection>
      {error && resubmit && (
        <FlexCenter>
          <Button onClick={resubmit}>Retry</Button>
        </FlexCenter>
      )}
      {error && !resubmit && (
        <Typography>
          Something went wrong with submitting your offer. Please refresh this
          page and try submitting your change order again.
        </Typography>
      )}
    </section>
  );
};

const BigModal = styled(Modal)`
  .modal-container {
    width: 95%;
  }
`;

const GapContainer = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  display: grid;
  gap: ${({ isMobile, isTablet }) => (isMobile || isTablet ? "8px" : "30px")};
`;

const GridContainer = styled.main<{ isMobile: boolean; isTablet: boolean }>`
  display: grid;
  justify-content: space-around;
  gap: 10px;
  grid-template-columns: ${({ isMobile, isTablet }) =>
    isMobile || isTablet ? "1fr" : "1fr 1fr 1fr"};
`;

const FlexCenter = styled.div`
  display: flex;
  justify-content: center;
`;

const MiddleContentSection = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  min-height: ${({ isMobile, isTablet }) =>
    isMobile || isTablet ? "60px" : "150px"};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: ${({ isMobile, isTablet }) => (isMobile || isTablet ? "0px" : "15px")};
  padding: ${({ isMobile, isTablet }) =>
    isMobile || isTablet ? "4px" : "10px"};
`;

export { RepricingLoadingWizard };
